import React, { useState, useEffect } from 'react';
import './Portfolios.css';
import ScrollIndicator from '../ScrollIndicator';
import TearFaceIcon from '../TearFaceIcon';
import InView, { ANIMATIONS } from '../InView';
import { useAnalytics } from '../../utils/analytics/useAnalytics';

const sections = [
  { id: 'portfolios-hero-section', label: 'Welcome' },
  { id: 'features-section', label: 'Why Choose Us' },
  { id: 'how-section', label: 'How we work' },
  { id: 'faq-section', label: 'FAQs' }
];

const features = [
  {
    id: 1,
    title: 'Beyond Portfolios, We Craft Stories',
    desc: "Transform your journey into a captivating narrative. Every pixel, animation, and interaction is meticulously crafted to showcase your unique story. We don't just display your work—we bring your professional journey to life in ways that leave lasting impressions.",
    image: '/images/whatImage1.jpg',
  },
  {
    id: 2,
    title: 'Smart Insights, Smarter Decisions',
    desc: "Unlock the power of engagement analytics. Watch in real-time as visitors interact with your work, understand what captures attention, and leverage these insights to continuously evolve your portfolio's impact. It's not just data—it's your competitive advantage.",
    image: '/images/feature2.jpg',
  },
  {
    id: 3,
    title: 'Your Corner of the Digital World',
    desc: 'Claim your space in the digital landscape with a personalized domain that reflects your brand. Whether at yourname.tearface.com or your custom domain, we ensure your portfolio becomes a powerful destination that draws clients and opportunities to you.',
    image: '/images/feature3.jpg',
  },
  {
    id: 4,
    title: 'Flawless on Every Screen',
    desc: 'Experience pixel-perfect excellence across all devices. From sprawling desktop displays to compact mobile screens, your portfolio maintains its stunning visual impact and smooth interactions. No compromises, just pure professional excellence everywhere.',
    image: '/images/feature1.jpg',
  },
];

const faqs = [
  {
    question: "How do payments work, and why no fixed costs?",
    answer: "We believe every portfolio is unique. Fixed pricing doesn't always account for custom needs, so we provide estimates based on your project's scope. Payments are tied to major milestones—like an initial deposit for starting work, design approval, or final deployment—ensuring transparency and fairness.",
  },
  {
    question: "How do you ensure my portfolio stays safe?",
    answer: "We prioritize security and functionality with HTTPS hosting, regular backups, and robust performance optimizations. Your portfolio is built to withstand traffic spikes, unauthorized changes, and other potential issues, giving you peace of mind.",
  },
  {
    question: "Can I update or expand my portfolio after it's launched?",
    answer: "Yes, whether it's small updates, adding new features, or expanding sections as your career grows. We offer management service packages, where the pricing depends on the scope of changes, ensuring it stays flexible to your needs.",
  },
  {
    question: "What kind of hosting do you offer?",
    answer: "We provide secure hosting with HTTPS, regular backups, and performance optimizations. You can choose a personalized subdomain (e.g., 'yourname.tearface.com') or connect a custom domain for your brand.",
  },
  {
    question: "How long does the process take?",
    answer: "Timelines vary based on project complexity. Custom designs take longer than pre-made concepts, but we'll collaborate closely to meet your deadlines. Our Bring Your Own Design tier can speed up delivery by focusing on development and deployment.",
  },
  {
    question: "Do I own my portfolio once it's live?",
    answer: "Yes, you retain full ownership of your portfolio. While we're here to support you, the final product belongs entirely to you.",
  },
  {
    question: "What if I'm not sure what I want in a portfolio?",
    answer: "No problem! We guide you through a discovery phase to identify your goals, style preferences, and the story you want to tell. From there, we create a portfolio that feels authentically you.",
  },
];

function StepCard({ icon, title, desc }) {
  return (
    <div className="step-card">
      <div className="step-icon">
        <img src={icon} alt={title} />
      </div>
      <h3>{title}</h3>
      <p>{desc}</p>
    </div>
  );
}

function Portfolios() {
  const { track } = useAnalytics();
  const [activeFaq, setActiveFaq] = useState(null);

  useEffect(() => {
    track('page_view', {
      category: 'Portfolios',
      label: 'Portfolios Page'
    });
  }, [track]);

  const toggleFaq = (i) => {
    setActiveFaq(activeFaq === i ? null : i);
  };

  const handleContactUs = () => {
    track('button_click', {
      category: 'Portfolios',
      label: 'Contact Us'
    });
    // Your existing contact us logic
  };

  const handleLearnMore = (portfolioItem) => {
    track('button_click', {
      category: 'Portfolios',
      label: portfolioItem.title
    });
    track('portfolio_view', portfolioItem);
    // Your existing learn more logic
  };

  const handleScroll = () => {
    track('scroll', {
      category: 'Portfolios',
      label: 'Portfolio Scroll'
    });
    // ... rest of the scroll logic ...
  };

  return (
    <>
      <ScrollIndicator sections={sections} />
      <TearFaceIcon />
      
      {/* Hero Section */}
      <div className='portfolios'>

        {/* Portfolios Hero Section */}
        <div className='portfolio-section portfolios-hero-section' id='portfolios-hero-section'>
          <div className='portfolio-hero-content'>
            <div className='portfolio-text'>
              <h1>
                <span className="portfolios-text">Portfolios</span>
                <span className="comma">,</span>
                <br />
                <span className="handcrafted-text">Handcrafted</span>
                <span className="dot">.</span>
              </h1>
              <p>
                Elevate your online presence with our handcrafted portfolios. 
                Each project is uniquely designed to tell your story and showcase your work in its best light.
              </p>
              <div className="portfolio-hero-buttons">
                <a href="/contact-us" className="portfolio-hero-button portfolio-hero-button-primary" onClick={handleContactUs}>
                  Contact Us
                </a>
                <a 
                  href="#faq-section" 
                  className="portfolio-hero-button portfolio-hero-button-secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('faq-section').scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Learn More
                </a>
              </div>
            </div>

            <div className='portfolio-card-container'>
              <div className="rotatingCard">
                <img src="/images/heroImage1.png" alt="Hero 1" className="card-image heroImage1" />
                <img src="/images/heroImage2.png" alt="Hero 2" className="card-image heroImage2" />
                <img src="/images/heroImage3.png" alt="Hero 3" className="card-image heroImage3" />
                <img src="/images/heroImage4.png" alt="Hero 4" className="card-image heroImage4" />
                <img src="/images/herosaying.png" alt="Overlay Text" className="overlay-text" />
              </div>
            </div>
          </div>
        </div>


        {/* Numbers Section */}
<div className="portfolio-section portfolios-numbers-section" id="portfolios-numbers-section">
  <div className="numbers-container">
    <a href="https://webflow.com/blog/0-prospects-to-36-interviews" target="_blank" rel="noopener noreferrer" className="number-item">
      <div className="number">3×</div>
      <div className="text">
        <span>More</span>
        <span>Interviews</span>
      </div>
    </a>
    <a href="https://www.researchgate.net/publication/352796546_Use_of_ePortfolios_for_making_hiring_decisions_a_comparison_of_the_results_from_representatives_of_large_and_small_businesses" target="_blank" rel="noopener noreferrer" className="number-item">
      <div className="number">72%</div>
      <div className="text">
        <span>Manager</span>
        <span>Approval</span>
      </div>
    </a>
    <a href="https://www.linearity.io/blog/web-design-statistics/" target="_blank" rel="noopener noreferrer" className="number-item">
      <div className="number">80%</div>
      <div className="text">
        <span>Higher</span>
        <span>Engagement</span>
      </div>
    </a>
    <a href="https://rgvdigitalmarketing.com/case-study-updated-design-yields-43-increase-in-conversion-rate" target="_blank" rel="noopener noreferrer" className="number-item">
      <div className="number">1 in 3</div>
      <div className="text">
        <span>Boost</span>
        <span>Conversions</span>
      </div>
    </a>
  </div>
</div>





        {/* Features Section */}
            <div className="portfolio-section portfolio-features-section" id="features-section">
              <InView animation={ANIMATIONS.FADE} delay={0.2}>
                <h2>
                  What Makes Us<span className="comma">,</span><br/> Stand Out<span className="dot">.</span>
                </h2>
              </InView>
              
              <div className="features-container">
                {features.map((feature, index) => (
                  <InView
                    key={feature.id}
                    animation={index % 2 === 0 ? ANIMATIONS.SLIDE_RIGHT : ANIMATIONS.SLIDE_LEFT}
                    delay={0.2 * (index + 1)}
                    threshold={0.3}
                  >
                    <div className={`feature-row ${index % 2 === 1 ? 'reverse' : ''}`}>
                      <div className="feature-image-container">
                        <img
                          src={feature.image}
                          alt={feature.title}
                          className="feature-image"
                          loading="lazy"
                        />
                      </div>
                      <InView animation={ANIMATIONS.SLIDE_UP} delay={0.4 * (index + 1)}>
                        <div className="feature-text-container">
                          <h3>{feature.title}</h3>
                          <p>{feature.desc}</p>
                        </div>
                      </InView>
                    </div>
                  </InView>
                ))}
              </div>
            </div>


    {/* How We Work Section */}
    <div className="portfolio-section how-section" id="how-section">
      <InView animation={ANIMATIONS.FADE} delay={0.2}>
        <h2>
          Vision to Reality<span className="comma">,</span><br/>
          Three Steps<span className="dot">.</span>
        </h2>
      </InView>
      
      <div className="step-process">
        <div className="step-cards">
          <StepCard
            icon='/icons/steps/design.svg'
            title="Design"
            desc="We craft your unique story into a stunning design"
          />
          <StepCard
            icon='/icons/steps/develop.svg'
            title="Develop"
            desc="Expert development brings your vision to life"
          />
          <StepCard
            icon='/icons/steps/deploy.svg'
            title="Deploy"
            desc="Launch your portfolio with full support"
          />
        </div>
      </div>
    </div>

    {/* FAQ Section */}
<div className="portfolio-section portfolio-faq-section" id="faq-section">
      <h2 className="portfolio-faq-title">
        Frequently Read<span className="comma">,</span><br/> Answers<span className="dot">.</span>
      </h2>
      
      <div className="portfolio-faq-container">
        {faqs.map((faq, i) => (
          <div
            key={i}
            className={`portfolio-faq-card ${activeFaq === i ? 'active' : ''}`}
            onClick={() => toggleFaq(i)}
          >
            <div className="portfolio-faq-question">{faq.question}</div>
            <div className="portfolio-faq-answer">{faq.answer}</div>
          </div>
        ))}
      </div>
    </div>


      </div>
    </>
  );
}

export default Portfolios;